import React, {useLayoutEffect, useRef, Fragment, useCallback} from "react";
import {observer} from "mobx-react";
import {ThemeProvider, Button, IconButton, Skeleton, Stack} from "@mui/material";
import {theme} from "assets/theming/theme";
import {MatchCard} from "views/components/match_card/match_card.component";
import {useViewController} from "data/services/locator";
import {IWidgetController} from "views/pages/widget/widget.controller";
import {Bindings} from "data/constants/bindings";
import styled from "@emotion/styled";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {FiltersComponent} from "views/components/filters/filters.component";
import {HOCLiveScore} from "views/components/hoc_live_score/hoc_live_score.component";
import {times} from "lodash";
import {Exist} from "views/components/exist/exist.component";
import {BettingPartner} from "views/components/betting_partner/betting_partner.component";

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	position: relative;
	z-index: 1;
	height: 100%;
`;

const LeftButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1;
	position: relative;
`;

const SliderButton = styled.div`
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 2;
	height: 145px;
	padding: 0 8px;
	transform: scale(0);
	transform-origin: center;

	animation: scaleUp 0.3s ease 0.3s forwards;

	button {
		box-shadow: 0 0.5px 1px rgba(28, 28, 30, 0.1), 0 4px 8px rgba(28, 28, 30, 0.2);
	}

	@media (max-width: 768px) {
		height: 125px;
		button {
			border-radius: 4px;
			width: 25px;
			height: 110px;
		}
	}
`;

const RightButton = styled(LeftButton)``;

const ScrollView = styled.div`
	overscroll-behavior: none;

	touch-action: cross-slide-x;
	overflow-x: auto;
	display: flex;
	width: 100%;
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
	scroll-snap-type: x mandatory;
	align-items: center;
	-webkit-overflow-scrolling: touch;
	position: relative;
	& > button {
		scroll-snap-align: start;

		&:last-of-type {
			scroll-snap-align: end;
		}
	}

	&::-webkit-scrollbar {
		display: none;
	}
`;

const Filters = styled(Button)`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 8px;
	gap: 5px;

	width: 50px;
	z-index: 2;
	height: 100%;
	text-transform: uppercase;
	font-family: var(--fontFamilySecond);
	font-style: normal;
	position: relative;
	pointer-events: auto;

	p {
		font-weight: 500;
		font-size: 12px;
		line-height: 150%;
	}

	span {
		font-weight: 600;
		font-size: 40px;
		word-break: break-all;
		line-height: 1;
	}

	svg {
		position: absolute;
		right: -4px;
		top: 50%;
		transform: translateY(-50%);
	}

	@media (max-width: 768px) {
		display: none;
	}
`;

const CustomWidthButton = styled.div<{match_count: number}>`
	min-width: 165px;
	max-width: 250px;
	width: calc(100% / ${(props) => props.match_count});

	@media (max-width: 768px) {
		width: 50%;
		min-width: 50%;
		& > div {
			max-width: 250px;
			margin: 0 auto;
		}
	}

	@media (max-width: 500px) {
		width: 100%;
		min-width: 100%;
		& > div {
			max-width: 250px;
			margin: 0 auto;
		}
	}
`;

const RowStack = styled(Stack)`
	flex-direction: row;
	gap: 8px;
	width: 100%;
	justify-content: space-between;

	span {
		max-width: 30px;
		height: 30px;
		width: 100%;

		&:nth-of-type(2) {
			flex: auto;
			max-width: 100%;
		}
	}
`;

const RowStackBorderTop = styled(Stack)`
	flex-direction: row;
	gap: 8px;
	width: 100%;
	justify-content: space-between;
	border-top: 1px solid rgba(18, 18, 18, 0.15);
`;
const SkeletonSlider = () => (
	<Fragment>
		{times(4, (n) => n).map((n) => (
			<CustomWidthButton match_count={2} key={n}>
				<Stack gap={"5px"} width={"100%"} maxWidth={"170px"} alignItems={"center"}>
					<Skeleton width={"80%"} height={"30px"} variant={"text"} />
					<RowStack>
						<Skeleton variant={"circular"} />
						<Skeleton variant={"text"} />
						<Skeleton variant={"rectangular"} />
					</RowStack>

					<RowStack>
						<Skeleton variant={"circular"} />
						<Skeleton variant={"text"} />
						<Skeleton variant={"rectangular"} />
					</RowStack>
					<RowStackBorderTop>
						<Skeleton width={"40%"} height={"30px"} variant={"text"} />
						<Skeleton width={"40%"} height={"30px"} variant={"text"} />
					</RowStackBorderTop>
				</Stack>
			</CustomWidthButton>
		))}
	</Fragment>
);
export const Widget: React.FC = observer(() => {
	const controller = useViewController<IWidgetController>(Bindings.WidgetController);

	const activeElement = useRef<HTMLDivElement | null>(null);

	const scrollToElement = useCallback(
		(active?: typeof activeElement.current | null) => {
			if (controller.scrollRef?.current) {
				if (active) {
					controller.scrollRef.current.scrollLeft = active.offsetLeft;
				} else {
					controller.scrollRef.current.scrollLeft = 0;
				}
			}
		},
		[controller.scrollRef]
	);

	useLayoutEffect(() => {
		scrollToElement(activeElement.current);
	}, [controller.currentMatch, scrollToElement]);

	const selectedMatchID = controller.currentMatch?.id;

	return (
		<ThemeProvider theme={theme}>
			<HOCLiveScore>
				<Wrapper>
					<LeftButton>
						<SliderButton>
							<IconButton
								color={"error"}
								onClick={controller.onLeftClick}
								disabled={!controller.hasPrevRound}>
								<ChevronLeftIcon />
							</IconButton>
						</SliderButton>

						<Filters
							variant={"customFilter"}
							color={controller.isOpenFilters ? "primary" : "secondary"}
							onClick={controller.toggleFilters}>
							<Exist when={controller.isLoading}>
								<Skeleton width={"40px"} height={"20px"} variant={"text"} />
								<Skeleton width={"40px"} height={"40px"} variant={"text"} />
							</Exist>
							<Exist when={!controller.isLoading}>
								<p>{controller.currentRoundPrefix}</p>
								<span>{controller.currentRoundName}</span>
								{controller.isOpenFilters ? <ArrowLeftIcon /> : <ArrowRightIcon />}
							</Exist>
						</Filters>

						<FiltersComponent isOpen={controller.isOpenFilters} />
					</LeftButton>
					<ScrollView ref={controller.scrollRef}>
						<Exist when={controller.isLoading}>
							<SkeletonSlider />
						</Exist>
						<Exist when={!controller.isLoading}>
							{controller.matches.map((match) => (
								<CustomWidthButton
									key={match.id}
									match_count={controller.matches.length}
									ref={selectedMatchID === match.id ? activeElement : null}
									onClick={(e) => {
										const tableRef = controller.scrollRef?.current;
										if (tableRef) {
											tableRef.scrollTo({
												left: e.currentTarget.offsetLeft,
												behavior: "smooth",
											});
										}
									}}>
									<MatchCard
										match={match}
										selectedTournamentId={controller.currentMatch?.id}
									/>
								</CustomWidthButton>
							))}
						</Exist>
					</ScrollView>

					<BettingPartner />

					<RightButton>
						<SliderButton>
							<IconButton
								color={"error"}
								onClick={controller.onRightClick}
								disabled={!controller.hasNextRound}>
								<ChevronRightIcon />
							</IconButton>
						</SliderButton>
					</RightButton>
				</Wrapper>
			</HOCLiveScore>
		</ThemeProvider>
	);
});

export default Widget;
